/**
 * Specific timezone-to-locale mappings that override region-based defaults.
 * Use this for timezones that need a specific locale to display proper abbreviations.
 *
 * @example
 * "Asia/Tokyo" uses "ja-JP" to display "JST" instead of "GMT+9"
 */
export const TIMEZONE_SPECIFIC_LOCALE: Record<string, string> = {
  "Asia/Tokyo": "ja-JP", // Japanese locale for JST
};

/**
 * Maps timezone region prefixes to appropriate locales for better abbreviation display.
 * This is used as a fallback when no specific timezone mapping exists.
 *
 * @example
 * const region = "Australia/Sydney".split("/")[0]; // "Australia"
 * const locale = TIMEZONE_REGION_TO_LOCALE[region]; // "en-AU"
 */
export const TIMEZONE_REGION_TO_LOCALE: Record<string, string> = {
  Australia: "en-AU",
  Pacific: "en-NZ",
  America: "en-US",
  Europe: "en-GB",
  Africa: "en-GB",
  Asia: "en-SG",
  Atlantic: "en-GB",
  Indian: "en-IN",
  Antarctica: "en-GB",
};

/**
 * Gets the appropriate locale for a given IANA timezone identifier.
 * First checks for specific timezone mappings, then falls back to region-based mapping.
 *
 * @param timezone - IANA timezone identifier (e.g., "Australia/Sydney", "America/New_York", "Asia/Tokyo")
 * @returns Locale string (e.g., "en-AU", "en-US", "ja-JP") or "en-US" as default
 *
 * @example
 * getLocaleForTimezone("Australia/Sydney"); // "en-AU" (from region mapping)
 * getLocaleForTimezone("America/New_York"); // "en-US" (from region mapping)
 * getLocaleForTimezone("Asia/Tokyo"); // "ja-JP" (from specific timezone mapping)
 * getLocaleForTimezone("Asia/Singapore"); // "en-SG" (from region mapping)
 * getLocaleForTimezone("UTC"); // "en-US" (default fallback)
 */
export const getLocaleForTimezone = (timezone: string): string => {
  // First check for specific timezone mapping
  if (TIMEZONE_SPECIFIC_LOCALE[timezone]) {
    return TIMEZONE_SPECIFIC_LOCALE[timezone];
  }

  // Fall back to region mapping
  const region = timezone.split("/")[0];
  return TIMEZONE_REGION_TO_LOCALE[region] || "en-US";
};
